import config from '../constants/config';

/**
 * Frontend Logger Module (Singleton)
 * 
 * This module maintains a circular buffer of log entries in memory.
 * Logs are only sent to the backend when a critical error occurs.
 * 
 * Features:
 * - Singleton pattern ensures only one logger instance
 * - Maintains a fixed-size buffer of log entries (FIFO)
 * - Provides console.log-like interface
 * - Formats logs for backend transmission
 * - Automatically removes oldest logs when buffer is full
 */

// Configuration
const MAX_LOG_ENTRIES = config.MAX_LOG_ENTRIES || 1000; // Maximum number of logs to keep in memory
const LOG_LEVELS = {
    DEBUG: 'debug',
    INFO: 'info',
    WARN: 'warn',
    ERROR: 'error'
};

/**
 * Logger class (Singleton)
 */
class Logger {
    constructor() {
        // Ensure singleton
        if (Logger.instance) {
            return Logger.instance;
        }

        // Initialize buffer
        this.logBuffer = [];

        // Initialize placeholder for original console (will be set by setupConsoleInterception)
        this._originalConsole = null;

        // Store instance
        Logger.instance = this;
    }

    /**
     * Formats a timestamp for log entries
     * @returns {string} Formatted timestamp
     */
    formatTimestamp() {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}`;
    }

    /**
     * Formats arguments to string (handles objects, arrays, etc.)
     * @param {...any} args - Arguments to format
     * @returns {string} Formatted string
     */
    formatArgs(...args) {
        return args.map(arg => {
            if (arg === null) return 'null';
            if (arg === undefined) return 'undefined';
            if (typeof arg === 'string') return arg;
            if (typeof arg === 'number' || typeof arg === 'boolean') return String(arg);
            if (arg instanceof Error) return `${arg.name}: ${arg.message}\n${arg.stack}`;
            // For objects and arrays, use JSON.stringify
            try {
                return JSON.stringify(arg, null, 2);
            } catch (error) {
                return String(arg);
            }
        }).join(' ');
    }

    /**
   * Adds a log entry to the buffer
   * @param {string} level - Log level
   * @param {...any} args - Arguments to log
   */
    addLog(level, ...args) {
        const timestamp = this.formatTimestamp();
        const message = this.formatArgs(...args);

        const logEntry = {
            timestamp,
            level,
            message
        };

        // Add to buffer
        this.logBuffer.push(logEntry);

        // Remove oldest entry if buffer is full
        if (this.logBuffer.length > MAX_LOG_ENTRIES) {
            this.logBuffer.shift(); // Remove first (oldest) entry
        }

        // Also log to console for development
        // Use original console if available (to prevent circular reference when console is intercepted)
        const consoleToUse = this._originalConsole || console;
        const consoleMethod = consoleToUse[level] || consoleToUse.log;
        consoleMethod(...args);
    }

    /**
     * Log debug message
     * @param {...any} args - Arguments to log
     */
    debug(...args) {
        this.addLog(LOG_LEVELS.DEBUG, ...args);
    }

    /**
     * Log info message
     * @param {...any} args - Arguments to log
     */
    info(...args) {
        this.addLog(LOG_LEVELS.INFO, ...args);
    }

    /**
     * Log warning message
     * @param {...any} args - Arguments to log
     */
    warn(...args) {
        this.addLog(LOG_LEVELS.WARN, ...args);
    }

    /**
     * Log error message
     * @param {...any} args - Arguments to log
     */
    error(...args) {
        this.addLog(LOG_LEVELS.ERROR, ...args);
    }

    /**
     * Generic log method (maps to info)
     * @param {...any} args - Arguments to log
     */
    log(...args) {
        this.addLog(LOG_LEVELS.INFO, ...args);
    }

    /**
     * Get all logs formatted as plain text
     * @returns {string} All logs formatted as plain text
     */
    getLogsAsPlainText() {
        return this.logBuffer.map(entry => {
            return `[${entry.timestamp}] ${entry.level}: ${entry.message}`;
        }).join('\n');
    }

    /**
     * Get the number of logs currently in buffer
     * @returns {number} Number of logs
     */
    getLogCount() {
        return this.logBuffer.length;
    }

    /**
     * Clear all logs from buffer
     */
    clearLogs() {
        this.logBuffer = [];
    }

    /**
   * Get authentication token from keycloak or localStorage
   * @returns {string|null} Authentication token
   */
    getToken() {
        try {
            // Try to get token from keycloak singleton first
            const KeycloakSingleton = require('../features/Auth/utils/keycloakSingleton').default;
            const keycloak = KeycloakSingleton.getInstance();

            // Check if keycloak is initialized and has a token
            if (keycloak && keycloak.token) {
                return keycloak.token;
            }
        } catch (error) {
            // Keycloak not available or not initialized
            console.warn('[Logger] Keycloak not available, falling back to localStorage');
        }

        // Fallback to localStorage (for guest scenarios)
        return localStorage.getItem('token');
    }

    /**
     * Send all logs to the backend
     * @param {string} [token] - Optional authentication token (if not provided, will auto-retrieve)
     * @returns {Promise<{success: boolean, incidentId?: string}>} Result object
     */
    async sendLogsToBackend(token) {
        try {
            const logs = this.getLogsAsPlainText();

            if (!logs) {
                console.warn('[Logger] No logs to send');
                return { success: false };
            }

            // Get token if not provided
            const authToken = token || this.getToken();

            if (!authToken) {
                console.error('[Logger] No authentication token available');
                return { success: false };
            }

            const response = await fetch(`${config.SERVER_URL}/api/log`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'text/plain'
                },
                body: logs
            });

            if (response.ok) {
                const data = await response.json();
                console.log('[Logger] Logs sent successfully, incident ID:', data.incidentId);
                return { success: true, incidentId: data.incidentId };
            } else {
                console.error('[Logger] Failed to send logs:', response.status);
                return { success: false };
            }
        } catch (error) {
            console.error('[Logger] Error sending logs:', error);
            return { success: false };
        }
    }
}

// Create the singleton instance
const logger = new Logger();

// Export the logger instance
export default logger;
